"use client";

import { AnalyticsActions, AnalyticsCategories, AnalyticsEventType, AnalyticsLabels } from "@/constants/analytics";
import { useEffect, useMemo } from "react";
import { sendGTMEvent } from "@/components/Tracking/TagManagerWrapper";
import { Pagination as ShadcnPagination, PaginationContent, PaginationItem, PaginationPrevious, PaginationNext, PaginationButton, PaginationEllipsis } from "@/components/ui/pagination";
/**
 * Component to show the Pagination for the home table.
 */
export function Pagination({
  items,
  itemsPerPage,
  currentPage,
  updateCurrentPage,
  showPageNumbersNextToCurrentPage = 1,
  pageNumbersLeftRight = 1
}: {
  items: number;
  itemsPerPage: number;
  currentPage: number;
  updateCurrentPage: (page: number) => void;
  pageNumbersLeftRight?: number; // default: 1, Amount of numbers to show at the highest and lowest possible page number.
  showPageNumbersNextToCurrentPage?: number; // default: 1, if currentpage === 4, page numbers 3 and 5 are shown. showPageNumbersNextToCurrentPage = 2 => page numbers 2,3,5 and 6 are shown.
}) {
  const maxPages = useMemo(() => Math.ceil(items / itemsPerPage), [items, itemsPerPage]);
  useEffect(() => {
    if (maxPages > 0 && currentPage > maxPages) {
      updateCurrentPage(maxPages);
    }
  }, [maxPages, currentPage, updateCurrentPage]);
  function setPage(page: number): void {
    if (page > 0 && page <= maxPages) {
      updateCurrentPage(page);
    }
  }
  const pageNumbers = useMemo(() => {
    const pn = [];
    for (let i = 1; i <= maxPages; i++) {
      // First condition: create all pages when there is only a gap of 1 page.
      // Second condition: create left pages
      // Third condition: create right pages
      // Fourth condition: create pages next to the current page
      if (i <= pageNumbersLeftRight || i > maxPages - pageNumbersLeftRight || i >= currentPage - showPageNumbersNextToCurrentPage && i <= currentPage + showPageNumbersNextToCurrentPage || maxPages > 5 && i === Math.floor(maxPages / 2)) {
        pn.push(i);
      }
    }
    return pn;
  }, [maxPages, currentPage, showPageNumbersNextToCurrentPage, pageNumbersLeftRight]);
  const pages = useMemo(() => {
    const pgs: React.ReactNode[] = [];
    pageNumbers.forEach((pn_0, i_0, arr) => {
      const nextNumber = arr.at(i_0 + 1);
      pgs.push(<PaginationItem key={pn_0}>
          <PaginationButton onClick={() => pageClick(pn_0)} disabled={pn_0 === currentPage ? true : undefined}>
            {pn_0}
          </PaginationButton>
        </PaginationItem>);
      if (nextNumber === pn_0 + 2) {
        // Insert normal number when there is a gap of 1
        <PaginationItem key={String(pn_0 + 1)}>
          <PaginationButton onClick={() => pageClick(pn_0 + 1)}>
            {pn_0}
          </PaginationButton>
        </PaginationItem>;
      }
      // Insert dots where multiple page numbers are skipped
      if (nextNumber && nextNumber > pn_0 + 2) {
        pgs.push(<PaginationItem key={"points" + String(pn_0 + 1)}>
            <PaginationEllipsis />
          </PaginationItem>);
      }
    });
    return pgs;
  }, [pageNumbers]);
  function pageClick(i_1: number) {
    setPage(i_1);
    sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.pagination, AnalyticsLabels.button, AnalyticsActions.pageClick, {
      value: String(i_1)
    });
  }
  function leftPageClick() {
    setPage(currentPage - 1);
    sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.pagination, AnalyticsLabels.button, AnalyticsActions.leftPageClick, {
      value: String(currentPage - 1)
    });
  }
  function rightPageClick() {
    setPage(currentPage + 1);
    sendGTMEvent(AnalyticsEventType.Event, AnalyticsCategories.pagination, AnalyticsLabels.button, AnalyticsActions.rightPageClick, {
      value: String(currentPage + 1)
    });
  }
  return <ShadcnPagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious onClick={leftPageClick} buttonStyle="button" disabled={currentPage === 1} />
        </PaginationItem>
        {...pages}
        <PaginationItem>
          <PaginationNext onClick={rightPageClick} buttonStyle="button" disabled={currentPage === maxPages} />
        </PaginationItem>
      </PaginationContent>
    </ShadcnPagination>;
}